import { z } from 'zod';
import { nonNegativePaiseSchema, paiseSchema } from '../money/paise.js';
import { objectIdSchema } from './common.js';
import {
  canonicalUnitSchema,
  milliUnitsSchema,
  nonNegativeMilliUnitsSchema,
  positiveMilliUnitsSchema,
  stockUnitSchema,
} from '../inventory/units.js';

/**
 * Stock, and every reason it moves (task 27).
 *
 * The shape that matters here is the LEDGER. There is no editable quantity field anywhere in
 * these types: an item's `quantity` is a computed view, and the only way to change it is to add
 * a movement with a reason, an actor and a timestamp. That is the whole reason a shop wants this
 * module — "where did four metres go?" — and it is a property of the schema rather than a
 * convention somebody has to remember.
 */

/** What a shop holds. Fabric is measured; the rest is counted. */
export const STOCK_CATEGORIES = [
  'fabric',
  'lining',
  'thread',
  'buttons',
  'zips',
  'laces',
  'beads',
  'other',
] as const;
export type StockCategory = (typeof STOCK_CATEGORIES)[number];
export const stockCategorySchema = z.enum(STOCK_CATEGORIES);

/**
 * Why stock moved. Every one of these is a different question somebody asks later, which is why
 * they are separate reasons rather than a signed number with a note.
 */
export const MOVEMENT_REASONS = [
  /** Bought in. Task 28's goods receipt writes these. */
  'purchase',
  /** Cut for an order. The only reason that attaches a cost to a garment. */
  'issue',
  /** Cut but not used, put back on the shelf. */
  'return',
  /**
   * Cut and unusable — offcuts, a mislaid measurement. Kept SEPARATE from consumption on
   * purpose: the wastage rate is where the money quietly leaks, and a shop cannot see a rate it
   * never recorded.
   */
  'wastage',
  /** Torn, stained, water-damaged. Not the shop's cutting, so not its wastage rate. */
  'damage',
  /** A stock-take correction. Always carries a note saying what was counted. */
  'adjustment',
  /** Sent back to the supplier. */
  'supplier_return',
  /**
   * Sold over the counter on an order's product line. Separate from `issue` because it is not
   * consumption: a metre sold is revenue, not a cost on a garment, and must not move the wastage
   * or material-cost figures.
   */
  'sale',
  /** A sale undone — the product line removed or the order cancelled. The mirror of `sale`. */
  'sale_return',
] as const;
export type MovementReason = (typeof MOVEMENT_REASONS)[number];
export const movementReasonSchema = z.enum(MOVEMENT_REASONS);

/** Which way each reason moves the shelf. The one place the sign of a movement is decided. */
export const MOVEMENT_DIRECTION: Readonly<Record<MovementReason, 1 | -1>> = Object.freeze({
  purchase: 1,
  issue: -1,
  return: 1,
  wastage: -1,
  damage: -1,
  // An adjustment carries its own sign — it is the one reason that can go either way.
  adjustment: 1,
  supplier_return: -1,
  sale: -1,
  sale_return: 1,
});

/**
 * Reasons only an order writes. The stock screen never offers them by hand, and `applyMovement`
 * lets `sale` take a variant below zero: the shelf count is often wrong, and a counter must never
 * be stopped from billing a customer because of it.
 */
export const ORDER_ONLY_MOVEMENT_REASONS: readonly MovementReason[] = [
  'issue',
  'return',
  'sale',
  'sale_return',
];

// --- Variants ------------------------------------------------------------------------------------

/**
 * The same fabric in another colour.
 *
 * A variant, not a second item, because everything an owner knows about the cloth — supplier,
 * rate, unit, reorder level — is the same for all of them, and duplicating the item to hold a
 * colour means editing the rate in six places when the supplier raises it.
 */
export const stockVariantSchema = z.object({
  id: z.string(),
  colour: z.string().trim().min(1).max(60),
  pattern: z.string().trim().max(60).nullable(),
  /** The supplier's own code for this exact cloth, which is what a reorder call quotes. */
  code: z.string().trim().max(40).nullable(),
  quantity: milliUnitsSchema,
  /** Photo of THIS colour. Fabric is recognised by sight far more reliably than by name. */
  photoMediaId: z.string().nullable(),
  archived: z.boolean(),
});
export type StockVariant = z.infer<typeof stockVariantSchema>;

export const createVariantSchema = z.object({
  colour: z.string().trim().min(1).max(60),
  pattern: z.string().trim().max(60).optional(),
  code: z.string().trim().max(40).optional(),
  photoMediaId: objectIdSchema.optional(),
});
export type CreateVariantInput = z.infer<typeof createVariantSchema>;

// --- Groups -------------------------------------------------------------------------------------

/**
 * The heading a shop reads its shelf under — "t shirt", "Buttons", "Lining".
 *
 * A group carries a UNIT because that is how a shop thinks of the heading itself: t shirt is
 * bought by the metre, buttons by the piece. It is a DEFAULT and nothing more — an item created
 * inside a group with no unit of its own takes it, and after that the item's own `unit` and
 * `canonicalUnit` are the only things any quantity is ever read against. Moving an item between
 * groups, or out of one, never restates what is on the shelf.
 *
 * A group holds no quantity. Adding one up from its items would be adding metres of lining to
 * pieces of button, and the number would mean nothing.
 */
export const stockGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** A media id from the ordinary pipeline, filed under `stock_group`. */
  imageMediaId: z.string().nullable(),
  /** The unit a new item in this group starts with. Never applied to an existing item. */
  unit: stockUnitSchema,
  active: z.boolean(),
  /** How many items sit under this heading right now. */
  itemCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type StockGroup = z.infer<typeof stockGroupSchema>;

export const createStockGroupSchema = z.object({
  name: z.string().trim().min(1).max(120),
  imageMediaId: objectIdSchema.nullable().optional(),
  unit: stockUnitSchema.default('metre'),
  active: z.boolean().default(true),
});
export type CreateStockGroupInput = z.infer<typeof createStockGroupSchema>;

export const updateStockGroupSchema = createStockGroupSchema
  .partial()
  .extend({ active: z.boolean().optional() });
export type UpdateStockGroupInput = z.infer<typeof updateStockGroupSchema>;

export const stockGroupListQuerySchema = z.object({
  /** Retired headings are hidden by default; the groups screen asks for them explicitly. */
  includeInactive: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value) => value === true || value === 'true')
    .default(false),
});
export type StockGroupListQuery = z.infer<typeof stockGroupListQuerySchema>;

export const stockGroupListSchema = z.object({ items: z.array(stockGroupSchema) });
export type StockGroupList = z.infer<typeof stockGroupListSchema>;

/** What deleting a heading did: the group is gone, and this many items were let go of it. */
export const deleteStockGroupResultSchema = z.object({
  id: z.string(),
  ungroupedItemCount: z.number().int(),
});
export type DeleteStockGroupResult = z.infer<typeof deleteStockGroupResultSchema>;

// --- The item -----------------------------------------------------------------------------------

export const stockItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: stockCategorySchema,
  /**
   * The heading this sits under, or null. OPTIONAL on purpose, in every direction: a shop that
   * never makes a group keeps a flat list, and an item written before groups existed reads back
   * as ungrouped rather than as broken.
   */
  groupId: z.string().nullable(),
  /** The group's name, denormalised so a list row needs no second fetch. */
  groupName: z.string().nullable(),
  /** What the shop buys and cuts in. Yards are stored as metres — see `units.ts`. */
  unit: stockUnitSchema,
  canonicalUnit: canonicalUnitSchema,
  supplierId: z.string().nullable(),
  supplierName: z.string().nullable(),
  /** Paise per whole unit, updated by a purchase at a different rate. */
  purchaseRatePaise: nonNegativePaiseSchema,
  reorderLevel: nonNegativeMilliUnitsSchema,
  location: z.string().nullable(),
  notes: z.string().nullable(),
  variants: z.array(stockVariantSchema),
  /** Every variant added up — what "do we have any of this?" means. */
  quantity: milliUnitsSchema,
  /** `quantity x purchaseRatePaise`, which is what the shelf is worth at cost. */
  valuePaise: paiseSchema,
  /** True when the total has fallen to or below the reorder level. */
  lowStock: z.boolean(),
  archived: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type StockItem = z.infer<typeof stockItemSchema>;

export const createStockItemSchema = z.object({
  name: z.string().trim().min(1).max(120),
  category: stockCategorySchema,
  /**
   * Left OPTIONAL rather than defaulted, so "not given" is distinguishable from "metre": an
   * item created inside a group with no unit of its own takes the group's. Absent both, the
   * service settles on metres, exactly as before.
   */
  unit: stockUnitSchema.optional(),
  groupId: objectIdSchema.nullable().optional(),
  supplierId: objectIdSchema.optional(),
  supplierName: z.string().trim().max(120).optional(),
  purchaseRatePaise: nonNegativePaiseSchema.default(0),
  reorderLevel: nonNegativeMilliUnitsSchema.default(0),
  location: z.string().trim().max(80).optional(),
  notes: z.string().trim().max(500).optional(),
  /** At least one, because a colourless roll of fabric is not a thing a shop can find. */
  variants: z.array(createVariantSchema).min(1).max(50),
});
export type CreateStockItemInput = z.infer<typeof createStockItemSchema>;

export const updateStockItemSchema = createStockItemSchema
  .omit({ variants: true })
  .partial()
  .extend({ archived: z.boolean().optional() });
export type UpdateStockItemInput = z.infer<typeof updateStockItemSchema>;

// --- Movements ------------------------------------------------------------------------------------

export const stockMovementSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  itemName: z.string(),
  variantId: z.string(),
  variantColour: z.string(),
  reason: movementReasonSchema,
  /** SIGNED, in canonical milli-units. The ledger sums this and nothing else. */
  quantity: milliUnitsSchema,
  /** Cost of this movement at the rate in force when it happened. */
  valuePaise: paiseSchema,
  ratePaise: nonNegativePaiseSchema,
  orderId: z.string().nullable(),
  orderItemId: z.string().nullable(),
  orderNumber: z.string().nullable(),
  purchaseOrderId: z.string().nullable(),
  note: z.string().nullable(),
  actorUserId: z.string().nullable(),
  actorName: z.string().nullable(),
  occurredAt: z.string(),
  /** The quantity the item held after this movement — what makes a history readable. */
  balanceAfter: milliUnitsSchema,
});
export type StockMovement = z.infer<typeof stockMovementSchema>;

/** A hand-recorded movement: a purchase without a PO, a stock-take, damaged cloth. */
export const recordMovementSchema = z.object({
  variantId: z.string().min(1),
  /** A sale and its reversal only ever come from an order's product line. */
  reason: movementReasonSchema.refine((reason) => reason !== 'sale' && reason !== 'sale_return', {
    message: 'Sales are recorded from the order',
  }),
  /** Always positive; the reason decides the sign. `adjustment` is the exception below. */
  quantity: positiveMilliUnitsSchema,
  /** Only for `adjustment`: true when the count came out LOWER than the ledger said. */
  decrease: z.boolean().default(false),
  ratePaise: nonNegativePaiseSchema.optional(),
  note: z.string().trim().max(300).optional(),
  occurredAt: z.union([z.date(), z.string()]).optional(),
});
export type RecordMovementInput = z.infer<typeof recordMovementSchema>;

/**
 * Cutting for an order.
 *
 * `wastage` rides along with the issue rather than being a second call, because that is how it
 * actually happens — the cutter takes 3.2 metres and 0.3 of it is offcut — and asking somebody
 * to remember a separate entry afterwards is asking for a wastage figure of zero forever.
 */
export const issueToOrderSchema = z.object({
  variantId: z.string().min(1),
  orderItemId: objectIdSchema,
  quantity: positiveMilliUnitsSchema,
  wastage: nonNegativeMilliUnitsSchema.default(0),
  note: z.string().trim().max(300).optional(),
});
export type IssueToOrderInput = z.infer<typeof issueToOrderSchema>;

export const returnToStockSchema = z.object({
  variantId: z.string().min(1),
  orderItemId: objectIdSchema,
  quantity: positiveMilliUnitsSchema,
  note: z.string().trim().max(300).optional(),
});
export type ReturnToStockInput = z.infer<typeof returnToStockSchema>;

// --- Views ---------------------------------------------------------------------------------------

export const stockListQuerySchema = z.object({
  q: z.string().trim().max(80).optional(),
  category: stockCategorySchema.optional(),
  /** One heading's items. Combines with everything else here rather than replacing it. */
  groupId: objectIdSchema.optional(),
  lowOnly: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value) => value === true || value === 'true')
    .default(false),
  includeArchived: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value) => value === true || value === 'true')
    .default(false),
});
export type StockListQuery = z.infer<typeof stockListQuerySchema>;

export const stockListSchema = z.object({
  items: z.array(stockItemSchema),
  totalValuePaise: paiseSchema,
  lowStockCount: z.number().int(),
});
export type StockList = z.infer<typeof stockListSchema>;

export const stockMovementListSchema = z.object({
  items: z.array(stockMovementSchema),
  /** The sum of the listed movements — which must equal the item's quantity when unfiltered. */
  netQuantity: milliUnitsSchema,
});
export type StockMovementList = z.infer<typeof stockMovementListSchema>;

export const wastageRowSchema = z.object({
  itemId: z.string(),
  itemName: z.string(),
  category: stockCategorySchema,
  unit: stockUnitSchema,
  issuedQuantity: milliUnitsSchema,
  wastedQuantity: milliUnitsSchema,
  damagedQuantity: milliUnitsSchema,
  wastedValuePaise: paiseSchema,
  /** wasted / (issued + wasted). Display only — never used to derive a rupee figure. */
  wastageRate: z.number(),
});
export type WastageRow = z.infer<typeof wastageRowSchema>;

export const wastageReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  rows: z.array(wastageRowSchema),
  totals: z.object({
    issuedQuantity: milliUnitsSchema,
    wastedQuantity: milliUnitsSchema,
    damagedQuantity: milliUnitsSchema,
    wastedValuePaise: paiseSchema,
    wastageRate: z.number(),
  }),
});
export type WastageReport = z.infer<typeof wastageReportSchema>;

export const slowMovingRowSchema = z.object({
  itemId: z.string(),
  itemName: z.string(),
  category: stockCategorySchema,
  unit: stockUnitSchema,
  quantity: milliUnitsSchema,
  valuePaise: paiseSchema,
  lastMovedAt: z.string().nullable(),
  daysSinceMoved: z.number().int().nullable(),
});
export type SlowMovingRow = z.infer<typeof slowMovingRowSchema>;

export const slowMovingReportSchema = z.object({
  sinceDays: z.number().int(),
  rows: z.array(slowMovingRowSchema),
  totalValuePaise: paiseSchema,
});
export type SlowMovingReport = z.infer<typeof slowMovingReportSchema>;

/** What an order item has had cut for it — the material cost behind a garment. */
export const orderMaterialSchema = z.object({
  orderItemId: z.string(),
  movements: z.array(stockMovementSchema),
  consumedQuantity: milliUnitsSchema,
  wastedQuantity: milliUnitsSchema,
  materialCostPaise: paiseSchema,
});
export type OrderMaterial = z.infer<typeof orderMaterialSchema>;
